import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Star, MessageSquare, TrendingUp, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { reviewsApi, type ReviewResponse } from "@/lib/api";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/reviews")({
  component: ReviewsPage,
});

function StarRating({ rating, size = "sm" }: { rating: number; size?: "sm" | "md" | "lg" }) {
  const s = size === "lg" ? "size-5" : size === "md" ? "size-4" : "size-3.5";
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${s} ${i <= rating ? "fill-gold text-gold" : "text-muted-foreground/30"}`}
        />
      ))}
    </div>
  );
}

function RatingBar({ star, count, total }: { star: number; count: number; total: number }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-3 text-muted-foreground font-medium">{star}</span>
      <Star className="size-3 fill-gold text-gold" />
      <progress
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted text-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary [&::-webkit-progress-value]:rounded-full [&::-moz-progress-bar]:bg-primary"
        value={count}
        max={total || 1}
      />
      <span className="w-6 text-right tabular-nums text-muted-foreground">{count}</span>
    </div>
  );
}

export default function ReviewsPage() {
  const [reviews, setReviews] = useState<ReviewResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "5" | "4" | "3" | "2" | "1">("all");
  const [actionId, setActionId] = useState<string | null>(null);

  useEffect(() => {
    reviewsApi
      .list({ pageSize: 100 })
      .then((res) => setReviews(res.data))
      .catch(() => toast.error("Failed to load reviews"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return reviews;
    if (filter === "pending") return reviews.filter((r: ReviewResponse) => !r.isApproved);
    if (filter === "approved") return reviews.filter((r: ReviewResponse) => r.isApproved);
    return reviews.filter((r: ReviewResponse) => r.rating === Number(filter));
  }, [reviews, filter]);

  const stats = useMemo(() => {
    const total = reviews.length;
    const avg = total ? reviews.reduce((s: number, r: ReviewResponse) => s + r.rating, 0) / total : 0;
    const counts = [0, 0, 0, 0, 0];
    reviews.forEach((r: ReviewResponse) => { counts[r.rating - 1]++; });
    return { total, avg, counts };
  }, [reviews]);

  const handleReviewAction = async (review: ReviewResponse, action: "approve" | "reject") => {
    setActionId(review.id);
    try {
      if (action === "approve") {
        await reviewsApi.approve(review.id);
        setReviews(prev => prev.map(item => item.id === review.id ? { ...item, isApproved: true } : item));
        toast.success("Review approved");
      } else {
        await reviewsApi.reject(review.id);
        setReviews(prev => prev.map(item => item.id === review.id ? { ...item, isApproved: false } : item));
        toast.success("Review hidden");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update review");
    } finally {
      setActionId(null);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, ReviewResponse[]>();
    for (const r of filtered) {
      const list = map.get(r.venueId) ?? [];
      list.push(r);
      map.set(r.venueId, list);
    }
    return map;
  }, [filtered]);

  return (
    <div className="mx-auto max-w-6xl animate-fade-up space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1 className="font-display text-xl font-bold">Guest Reviews</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Reviews left by guests across your venues.
        </p>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft animate-pulse">
              <div className="h-10 w-10 bg-muted rounded-lg mb-3" />
              <div className="h-7 w-16 bg-muted rounded mb-1" />
              <div className="h-3 w-24 bg-muted rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary mb-3">
              <MessageSquare className="size-5" />
            </div>
            <p className="text-xl font-bold">{stats.total}</p>
            <p className="text-xs font-medium text-foreground mt-1">Total Reviews</p>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary mb-3">
              <Star className="size-5" />
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-xl font-bold">{stats.avg.toFixed(1)}</p>
              <span className="text-sm text-muted-foreground">/ 5</span>
            </div>
            <p className="text-xs font-medium text-foreground mt-1">Average Rating</p>
            <div className="mt-1">
              <StarRating rating={Math.round(stats.avg)} />
            </div>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary mb-3">
              <TrendingUp className="size-5" />
            </div>
            <p className="text-xl font-bold">{stats.counts[4] + stats.counts[3]}</p>
            <p className="text-xs font-medium text-foreground mt-1">4-5 Star Reviews</p>
            <p className="text-[11px] text-muted-foreground">
              {stats.total ? Math.round(((stats.counts[4] + stats.counts[3]) / stats.total) * 100) : 0}% of total
            </p>
          </div>
          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <div className="grid size-10 place-items-center rounded-lg bg-accent text-primary mb-3">
              <MessageSquare className="size-5" />
            </div>
            <p className="text-xl font-bold">{stats.total > 0 ? "Active" : "—"}</p>
            <p className="text-xs font-medium text-foreground mt-1">Review Stream</p>
            <p className="text-[11px] text-muted-foreground">Live from database</p>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
        {/* Sidebar: Breakdown + Filter */}
        <aside className="space-y-6">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft">
            <h3 className="font-display text-base font-semibold mb-4">Rating Breakdown</h3>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => (
                <RatingBar
                  key={star}
                  star={star}
                  count={stats.counts[star - 1]}
                  total={stats.total}
                />
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft">
            <h3 className="font-display text-sm font-semibold mb-3">Filter by Rating</h3>
            <div className="flex flex-wrap gap-2">
              {(["all", "pending", "approved", "5", "4", "3", "2", "1"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${
                    filter === f
                      ? "bg-primary text-primary-foreground"
                      : "bg-accent text-foreground hover:bg-primary/10"
                  }`}
                >
                  {f === "all" ? "All" : f === "pending" ? "Pending" : f === "approved" ? "Approved" : `${f} stars`}
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Reviews List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : stats.total === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border py-20 text-center">
              <MessageSquare className="mb-3 size-12 text-muted-foreground/40" />
              <p className="text-lg font-semibold">No reviews yet</p>
              <p className="mt-1 text-sm text-muted-foreground max-w-xs">
                Reviews left by guests on your venue pages will appear here.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-16 text-center">
              <p className="text-sm text-muted-foreground">No reviews match this filter.</p>
            </div>
          ) : (
            Array.from(grouped.entries()).map(([venueId, venueReviews]) => (
              <div key={venueId} className="space-y-3">
                <div className="flex items-center gap-3 pb-2 border-b border-border/40">
                  <div className="size-10 rounded-lg bg-accent grid place-items-center text-primary font-semibold text-sm">
                    {venueReviews[0].venueName.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{venueReviews[0].venueName}</p>
                  </div>
                  <span className="ml-auto text-xs text-muted-foreground bg-accent rounded-full px-2 py-0.5">
                    {venueReviews.length} review{venueReviews.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div className="space-y-3">
                  {venueReviews.map((r) => (
                    <article
                      key={r.id}
                      className="rounded-2xl border border-border/60 bg-card p-5 shadow-soft"
                    >
                      <header className="flex items-start gap-3">
                        <span className="grid size-10 place-items-center rounded-full bg-primary/10 font-semibold text-primary shrink-0">
                          {r.venueName.slice(0, 2).toUpperCase()}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-medium text-sm">Guest</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(r.createdAt).toLocaleDateString("en-NG", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                          </div>
                          <div className="mt-0.5">
                            <StarRating rating={r.rating} />
                          </div>
                        </div>
                      </header>
                      <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{r.comment}</p>
                      <div className="mt-4 flex flex-wrap items-center gap-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${r.isApproved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                          {r.isApproved ? "Approved" : "Pending"}
                        </span>
                        {!r.isApproved ? (
                          <button
                            type="button"
                            onClick={() => handleReviewAction(r, "approve")}
                            disabled={actionId === r.id}
                            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {actionId === r.id ? <Loader2 className="size-3 animate-spin" /> : <CheckCircle2 className="size-3" />}
                            Approve
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleReviewAction(r, "reject")}
                            disabled={actionId === r.id}
                            className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {actionId === r.id ? <Loader2 className="size-3 animate-spin" /> : <XCircle className="size-3" />}
                            Hide
                          </button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}



